import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import StyleConfig from 'src/constants/StyleConfig';

interface CommonProps {
  buttonColor?: 'primary' | 'white' | 'secondary' | 'green' | 'red';
  isDisabled: boolean;
  text: string;
  textAlign?: 'left' | 'center';
  textColor?: 'green' | 'red';
  borderColor?: 'green' | 'red' | 'gray';
  hasIcon?: boolean;
}

type Props = CommonProps & {
  type?: 'submit' | 'button' | 'reset';
  clickEvent: Function;
};

type LinkProps = CommonProps & {
  url: string;
};

const Button = ({ ...props }: Props) => {
  return (
    <StyledButton
      type={props.type ? props.type : 'submit'}
      onClick={() => {
        props.clickEvent();
      }}
      disabled={props.isDisabled}
      buttonColor={props.buttonColor}
      textColor={props.textColor}
      borderColor={props.borderColor}
      hasIcon={props.hasIcon}
    >
      <span>{props.text}</span>
    </StyledButton>
  );
};

const LinkButton = ({ ...props }: LinkProps) => {
  return (
    <Styled
      disabled={props.isDisabled}
      buttonColor={props.buttonColor}
      textColor={props.textColor}
      borderColor={props.borderColor}
      textAlign={props.textAlign}
      hasIcon={props.hasIcon}
    >
      <Link to={props.url}>{props.text}</Link>
    </Styled>
  );
};

export interface StyledProps {
  disabled: boolean;
  buttonColor?: 'primary' | 'white' | 'secondary' | 'green' | 'red';
  textAlign?: 'left' | 'center';
  textColor?: 'green' | 'red';
  borderColor?: 'green' | 'red' | 'gray';
  hasIcon?: boolean;
}

const getButtonColor = ({ disabled, buttonColor }: StyledProps) => {
  if (disabled) {
    return StyleConfig.COLOR.BTN_DISABLED;
  }
  switch (buttonColor) {
    case 'primary':
    default:
      return StyleConfig.COLOR.PRIMARY;
    case 'secondary':
      return StyleConfig.COLOR.SECONDORY;
    case 'white':
      return '#fff';
    case 'green':
      return StyleConfig.COLOR.GREEN;
    case 'red':
      return '#FF0303';
  }
};

export const Styled = styled.div<StyledProps>`
  width: 100%;
  font-weight: bold;
  position: relative;
  border-radius: 4px;
  background-color: ${({ disabled, buttonColor }) => {
    return getButtonColor({ disabled, buttonColor });
  }};
  border: 1px solid
    ${({ borderColor }) => {
      if (borderColor === 'gray') {
        return StyleConfig.COLOR.INPUT_BORDER;
      }
      if (borderColor === 'green') {
        return StyleConfig.COLOR.GREEN;
      }
      if (borderColor === 'red') {
        return '#FF0303';
      }
      return 'transparent';
    }};
  text-align: ${({ textAlign }) => (textAlign ? textAlign : 'center')};
  > span,
  a {
    position: relative;
    color: ${({ buttonColor, textColor, borderColor }) => {
      if (borderColor === 'gray') {
        return StyleConfig.COLOR.TEXT_COLOR;
      }
      if (textColor === 'green') {
        return StyleConfig.COLOR.GREEN;
      }
      if (textColor === 'red') {
        return '#FF0303';
      }
      if (buttonColor === 'white') {
        return StyleConfig.COLOR.PRIMARY;
      }
      return '#fff';
    }};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
    display: inline-block;
    text-decoration: none;
    width: 100%;
    height: 100%;
    padding: 12px;
    ${({ hasIcon, buttonColor, textColor }) => {
      if (hasIcon) {
        let path = `/images/icon/circle_arrow_w.png`;
        if (buttonColor === 'white') {
          path = `/images/icon/circle_arrow_b.png`;
        }
        if (buttonColor === 'green') {
          path = `/images/icon/circle_arrow_g.svg`;
        }
        if (textColor === 'green') {
          path = `/images/icon/circle_arrow_gb.svg`;
        }
        return `
        &:after{
          position: absolute;
          top: 0;
          bottom: 0;
          right: 12px;
          margin: auto;
          content: '';
          display: block;
          background-image: url(${path});
          background-size: cover;
          width: 20px;
          height: 20px;
        }
        `;
      }
    }};
  }
`;
const StyledButton = Styled.withComponent('button');

export { LinkButton };
export default Button;
